import { Component, Input, Output, EventEmitter, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ColDef } from 'ag-grid-community';
import { GridComponent } from 'src/app/shared/AG-grid/grid-component/grid.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AgGridModule } from 'ag-grid-angular';
import { SharedModule } from 'src/app/shared.module';
import { ButtonComponent } from 'src/app/shared/design-system/button/button.component';
import { PanelConfig } from 'src/app/shared/design-system/panel/panel.modal';
import { ButtonConfig } from 'src/app/shared/design-system/button/button.modal';
import { PanelComponent } from 'src/app/shared/design-system/panel/panel.component';
import { ProjectBudgetService } from 'src/app/_services/project-budget/project-budget.service';
import { first, take } from 'rxjs/operators';
import { AlertService } from 'src/app/alert-panel/alert.service';
import { GridConfig } from 'src/app/_models/common/grid/grid-config';

@Component({
    selector: 'select-entity-approver-panel',
    templateUrl: './select-entity-approver-panel.component.html',
    standalone: true,
    styles: [`:host ::ng-deep .ds-side__panel { width: 40vw !important; }`],
    imports: [TranslateModule,
        CommonModule,
        PanelComponent,
        ButtonComponent, GridComponent,
    ]
})
export class SelectEntityApproverPanelComponent implements OnInit {

    @Input() inputData: any;
    @Output() closePanelEvent = new EventEmitter<boolean>();
    @Output() selectItemEvent = new EventEmitter<any>();

    @ViewChild(GridComponent) gridComponent: GridComponent;

    approversData: any[] = [];
    gridData: any[] = [];
    // filterByLabel: string = 'Filter By';

    currentSearchText: string = '';
    columnDefs: ColDef[] = [];
    gridConfigItems: GridConfig;

    private translate = inject(TranslateService);
    private cdr = inject(ChangeDetectorRef);
    private alertService = inject(AlertService);

    panelConfig: PanelConfig = {
        id: 'setup-entity-approvers-panel',
        title: this.translate.instant('pb_select_approver'),
        size: 'lg',
        position: 'right',
        hasOverlay: true,
        projectedContent: true,
    };

    saveButtonConfig: ButtonConfig = {
        id: 'save-entity-approvers--btn',
        label: this.translate.instant('label_save'),
        color: "primary",
        size: "lg",
        disabled: true,
        ariaLabel: this.translate.instant('label_save')
    };

    constructor(private projectBudgetService: ProjectBudgetService) { }

    ngOnInit(): void {
        this.fnInitialiseGrid();
        this.loadAvailableApprovers();


    }

    fnInitialiseGrid() {
        this.fnCreateColumnDefs();
        this.fnGetGridConfigItems();
        setTimeout(() => {
            this.gridComponent.initializeGrid();
        }, 100);
    }

    fnCreateColumnDefs() {
        this.columnDefs = [
            {
                headerName: this.translate.instant('name'),
                field: 'approverName',
                sortable: true,
                resizable: true,
                suppressHeaderMenuButton: true,
                minWidth: 120
            }
        ];
    }

    fnGetGridConfigItems() {
        this.gridConfigItems = new GridConfig(
            true,
            true,
            "updatedOn",
            "approverId",
            0,
            true,
            [],
            true,
            true,
            true,
            null,
            "desc",
            40,
            "no_data_availabe",
            null,
            null,
            false,
            false,
            "",
            "",
            false
        );
        this.gridConfigItems.showFilter = false;
    }

    private loadAvailableApprovers(): void {
        this.projectBudgetService.GetAvailableApproverList()
            .pipe(first())
            .subscribe(res => {
                const list = res?.responseInfo?.dropdownApproverList ?? [];
                this.approversData = list.map(a => ({
                    approverId: a.entityApproverId,
                    approverName: a.approverName,
                    selected: false
                }));
                this.fnPreSelectApprovers();
                this.gridData = this.approversData.slice();
                this.cdr.detectChanges();          // flush [data] -> grid ngOnChanges -> datasource
                this.gridComponent.refreshGridData();
            });
    }

    fnPreSelectApprovers(): void {
        if (!this.inputData?.selectedApprovers?.length) {
            return;
        }
        const selectedIds = new Set<number>(this.inputData.selectedApprovers.map((a: any) => a.approverId));
        this.approversData.forEach(a => {
            a.selected = selectedIds.has(a.approverId);
        });
    }

    fnOnGridCellClick(params) {
        if (params.column?.colId !== 'selected') return;
        if (!params.data) return;
        this.saveButtonConfig = { ...this.saveButtonConfig, disabled: false };
        this.gridComponent.gridApi?.redrawRows();
        this.cdr.detectChanges();
    }

    fnOnGridCheckAll(params) {
        this.saveButtonConfig = { ...this.saveButtonConfig, disabled: false };

    }

    onSaveBtnClicked() {
        const selectedRows = this.gridData.filter(a => a.selected);
        const outputModel = {
            selectedApprovers: selectedRows.map(row => ({
                approverId: row.approverId,
                approverName: row.approverName
            }))
        };
        this.selectItemEvent.emit(outputModel);
        this.emitClose();
    }

    // onSearchChanged(value: string) {
    //     this.currentSearchText = value ? value.toString().trim() : '';
    //     this.applyFilters();
    // }

    // private applyFilters() {
    //     let filtered = this.approversData.slice();
    //     const text = this.currentSearchText && this.currentSearchText.toLowerCase();
    //     if (text) {
    //         filtered = filtered.filter(row =>
    //             row.approverName.toLowerCase().indexOf(text) !== -1
    //         );
    //     }
    //     this.gridData = filtered;
    // }

    /** True when the user has edits that would be lost on close. */
    private isDirty(): boolean {
        return !this.saveButtonConfig.disabled;
    }

    onClose(): void {
        if (!this.isDirty()) {
            this.emitClose();
            return;
        }
        // Ask the global unsaved-changes modal (rendered by app.component) and act on the answer.
        // take(1) keeps this scoped to the panel that asked: the underlying subject is static, so a
        // long-lived subscription here would also fire for nested panels and close the whole stack.
        this.alertService.getUserActionEvent()
            .pipe(take(1))
            .subscribe(discard => {
                if (discard) { this.emitClose(); }
            });
        this.alertService.onFormClosed();
    }

    /** Closes without asking — for post-save and confirmed-discard paths. */
    private emitClose(): void {
        this.closePanelEvent.emit(true);
    }
}
